"use client";

import { useEffect, useState } from "react";
import "./careers.css";
import { careerImages } from "./careers-image-config";
import {
  BulbIcon,
  UsersIcon,
  GlobeIcon,
  LinkIcon,
  ChartIcon,
  TargetIcon,
  PlayIcon,
  PinIcon,
  ArrowRightIcon,
  HeartIcon,
  ScaleIcon,
  FemaleIcon,
  GraduationCapIcon,
  BriefcaseIcon,
  AwardIcon,
} from "./icons";

const benefits = [
  [BulbIcon, "Meaningful Work", "Solve real business problems for real customers."],
  [UsersIcon, "Learning & Growth", "Work with SAP and AI industry experts."],
  [GlobeIcon, "Global Opportunities", "Be part of a truly global delivery network."],
  [LinkIcon, "Collaborative Culture", "A people-first working environment."],
  [ChartIcon, "Career Progression", "Clear career paths and professional growth."],
  [TargetIcon, "Purpose & Impact", "Create value for businesses and communities."]
] as const;

const pathways = [
  [GraduationCapIcon, "Students & Freshers", "Start your journey with structured learning and real project exposure."],
  [BriefcaseIcon, "Consultants", "Build domain and technology expertise across industries."],
  [AwardIcon, "Senior Consultants", "Lead modules, clients, and teams."],
  [UsersIcon, "Managers", "Drive delivery success and people development."],
  [ChartIcon, "Directors", "Shape solutions, business strategy, and client relationships."],
  [LinkIcon, "Partners", "Build industries, clients, and a better tomorrow."]
] as const;

const openings = [
  ["SAP S/4HANA Consultant", "Noida / Remote"],
  ["SAP BTP / Integration Consultant", "Noida / Remote"],
  ["SuccessFactors Consultant", "Noida / Remote"],
  ["Data & AI Consultant", "Noida / Remote"],
  ["Industry Solution Lead", "Noida / Remote"],
  ["Project Manager", "Noida / Client Site"]
];

const testimonials = [
  {
    quote: "At Orane, I get to work on real projects, learn every day, and be part of a team that genuinely cares.",
    name: "Employee Name",
    role: "Senior Consultant, SAP S/4HANA",
  },
  {
    quote: "The variety of client problems here keeps me learning constantly, and the people make the hard days easier.",
    name: "Employee Name",
    role: "Consultant, Data & AI",
  },
  {
    quote: "I moved from a support role into consulting within a year — the growth path here is real, not just talk.",
    name: "Employee Name",
    role: "Associate Consultant, SuccessFactors",
  },
];

export default function CareersPage() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [testimonialDirection, setTestimonialDirection] = useState<"next" | "prev">("next");

  const showPrevTestimonial = () => {
    setTestimonialDirection("prev");
    setActiveTestimonial((index) => (index === 0 ? testimonials.length - 1 : index - 1));
  };

  const showNextTestimonial = () => {
    setTestimonialDirection("next");
    setActiveTestimonial((index) => (index === testimonials.length - 1 ? 0 : index + 1));
  };

  useEffect(() => {
    const cards = document.querySelectorAll(
      ".career-benefit-card, .career-gallery-item, .career-pathway-step-inner, .career-opening-row, .career-diversity-grid > div"
    );

    if (!("IntersectionObserver" in window)) {
      cards.forEach((card) => card.classList.add("in-view"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );

    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <main className="careers-page">

      {/* HERO */}
      <section className="career-hero career-container">

        <div
          className="career-hero-top"
          style={{ backgroundImage: `url(${careerImages.hero})` }}
        >
          <div className="career-hero-content">
            {/* <span className="career-eyebrow">CAREERS AT ORANE</span> */}

            <h1>
              Build a Career That Creates
              <br />
              <span>Real Value.</span>
            </h1>

            <p>
              Work on real business problems. Learn continuously.
              Grow with people who make a difference — with SAP,
              Data and AI.
            </p>

            <div className="career-hero-buttons">
              <a href="#openings" className="career-btn career-btn-primary">
                Explore Opportunities
                <ArrowRightIcon className="career-btn-icon" />
              </a>

              <a href="#culture" className="career-btn career-btn-outline">
                <PlayIcon className="career-btn-icon" />
                Hear From Our People
              </a>
            </div>
          </div>

          <div className="career-hero-stats">
            <div>
              <strong>15+</strong>
              <span>Years</span>
            </div>

            <div>
              <strong>300+</strong>
              <span>Minds</span>
            </div>

            <div>
              <strong>Global</strong>
              <span>Delivery</span>
            </div>

            <div>
              <strong>Real</strong>
              <span>Outcomes</span>
            </div>
          </div>
        </div>

      </section>


      {/* BENEFITS */}
      <section className="career-benefits-section career-container">

        <h2>Why People Choose Orane</h2>

        <div className="career-benefits-grid">
          {benefits.map(([Icon, title, description]) => (
            <div className="career-benefit-card" key={title}>
              <div className="career-benefit-icon">
                <Icon />
              </div>
              <h3>{title}</h3>
              <p>{description}</p>
            </div>
          ))}
        </div>

      </section>


      {/* CULTURE */}
      <section className="career-culture-section career-container" id="culture">

        <div className="career-culture-text">
          <span className="career-eyebrow">LIFE AT ORANE</span>

          <h2>
            Great Work.
            <br />
            Good People.
            <br />
            <span>A Brighter Tomorrow.</span>
          </h2>

          <p>
            From client projects to learning sessions,
            hackathons to cultural events — life at Orane
            is about growth, collaboration and impact.
          </p>

          <a href="#culture" className="career-btn career-btn-outline">
            <PlayIcon className="career-btn-icon" />
            Watch Our Culture Video
          </a>
        </div>

        <div className="career-culture-gallery">
          <div className="career-gallery-item career-gallery-item-tall">
            <img src={careerImages.culture1} alt="Orane team" />
          </div>
          <div className="career-gallery-item">
            <img src={careerImages.culture2} alt="Team collaboration" />
          </div>
          <div className="career-gallery-item">
            <img src={careerImages.culture3} alt="Orane employees" />
          </div>
          <div className="career-gallery-item">
            <img src={careerImages.culture4} alt="Company culture" />
          </div>
          <div className="career-gallery-item">
            <img src={careerImages.culture5} alt="Team celebrating together" />
          </div>
        </div>

      </section>


      {/* CAREER PATHWAYS */}
      <section className="career-pathways-section career-container">

        <div className="career-section-heading-row">
          <div>
            <h2>Career Pathways</h2>
            <p>
              Whether you are starting your career or bringing
              deep experience, there is a place for you at Orane.
            </p>
          </div>

          <a href="#openings">
            Explore Career Paths
            <ArrowRightIcon className="career-link-icon" />
          </a>
        </div>

        <div className="career-pathways-grid">
          {pathways.map(([Icon, title, description], index) => (
            <div className="career-pathway-step" key={title}>
              <div className="career-pathway-step-inner">
                <div className="career-pathway-icon">
                  <Icon />
                </div>

                <h3>{title}</h3>

                <p>{description}</p>
              </div>

              {index < pathways.length - 1 && (
                <span className="career-pathway-connector">
                  <ArrowRightIcon />
                </span>
              )}
            </div>
          ))}
        </div>

      </section>


      {/* OPENINGS + DIVERSITY */}
      <section className="career-details career-container" id="openings">

        <div className="career-openings-section">

          <div className="career-section-heading-row">
            <h2>Current Openings</h2>
            <a href="#openings">
              View All Openings
              <ArrowRightIcon className="career-link-icon" />
            </a>
          </div>

          <p>Join our growing global team.</p>

          <div className="career-openings-list">
            {openings.map(([title, location]) => (
              <div className="career-opening-row" key={title}>

                <strong>{title}</strong>

                <span>
                  <PinIcon />
                  {location}
                </span>

                <span>Full-time</span>

                <a href="#apply">
                  <ArrowRightIcon />
                </a>

              </div>
            ))}
          </div>

        </div>


        <div className="career-diversity-section">

          <div className="career-section-heading-row">
            <h2>Diversity, Inclusion and Belonging</h2>
            <a href="#diversity">
              Learn More
              <ArrowRightIcon className="career-link-icon" />
            </a>
          </div>

          <p>Different backgrounds. One shared purpose.</p>

          <div className="career-diversity-grid">

            <div>
              <span><HeartIcon /></span>
              <strong>Inclusive<br />Workplace</strong>
            </div>

            <div>
              <span><ScaleIcon /></span>
              <strong>Equal<br />Opportunities</strong>
            </div>

            <div>
              <span><FemaleIcon /></span>
              <strong>Women<br />in Tech</strong>
            </div>

            <div>
              <span><GlobeIcon /></span>
              <strong>Global<br />Teams</strong>
            </div>

          </div>

          <div
            className={`career-testimonial career-testimonial-slide-${testimonialDirection}`}
            key={activeTestimonial}
          >
            <img src={careerImages.employee} alt="Orane employee" />

            <div>
              <p>&ldquo;{testimonials[activeTestimonial].quote}&rdquo;</p>

              <strong>{testimonials[activeTestimonial].name}</strong>
              <span>{testimonials[activeTestimonial].role}</span>
            </div>
          </div>

          <div className="career-testimonial-nav">
            <button
              type="button"
              className="career-testimonial-arrow career-testimonial-arrow-prev"
              aria-label="Previous testimonial"
              onClick={showPrevTestimonial}
            >
              <ArrowRightIcon />
            </button>

            <div className="career-testimonial-dots">
              {testimonials.map((testimonial, index) => (
                <button
                  type="button"
                  key={testimonial.name + index}
                  className={index === activeTestimonial ? "active" : undefined}
                  aria-label={`Show testimonial ${index + 1}`}
                  onClick={() => {
                    setTestimonialDirection(index > activeTestimonial ? "next" : "prev");
                    setActiveTestimonial(index);
                  }}
                />
              ))}
            </div>

            <button
              type="button"
              className="career-testimonial-arrow career-testimonial-arrow-next"
              aria-label="Next testimonial"
              onClick={showNextTestimonial}
            >
              <ArrowRightIcon />
            </button>
          </div>

        </div>

      </section>


      {/* FINAL CTA */}
      <section
        className="final-career-cta career-container"
        style={{ backgroundImage: `url(${careerImages.cta})` }}
      >

        <div>
          <span className="career-eyebrow">READY TO BUILD WHAT&apos;S NEXT?</span>

          <h2>Explore Opportunities at Orane.</h2>

          <p>
            Bring your skills, ideas and curiosity.
            Let&apos;s create a more valuable tomorrow — together.
          </p>
        </div>

        <a href="/tellusyourproblem#tell-details" className="career-btn career-btn-white career-cta-btn">
          Join Our Team
          <ArrowRightIcon className="career-btn-icon" />
        </a>

      </section>

    </main>
  );
}